/**
 * @fileoverview Adding and editing a gardener's own to-do (D50, batch 6 of the
 * to-do rebuild), for the sheet wherever it opens (Today, a plant's card, a
 * bed's card). The server places, words and keeps the to-do; it answers as a
 * mark is answered (api/ownTodo; B73): at once, once saved, with no brief. The
 * page reads Today again in the background (the new card shows everywhere
 * when it lands, a moment later), and what the answer says
 * (the toast, with Undo, in the server's words: "On Today: Tie up the vines ·
 * Undo"; "Saved."). Undo is a mark the server names (remove), made through the
 * page's useTodoMarks.
 *
 * openKey is the last to-do added or edited (the answer's said.openKey):
 * Today's groups show it even past "3 more" and scroll to it (Fable pass 2:
 * a to-do just added is never hidden behind "more").
 *
 * A failure on our side (a 5xx, no connection) says the standard sentence and
 * is reported (D31, lib/serverFailure); a to-do that is no longer there (the
 * server's 404 with TODO_JOB_NOT_FOUND) reads the brief again and says so
 * (todo.gone); a 404 without that code (a route the server does not have) is a
 * failure on our side: reported, and the standard sentence (cleanup C59); any
 * other refusal says todo.own.notSaved. The sheet keeps what was typed.
 *
 * Where: features/own-todos OwnTodoSheet (useOwnTodoSheet).
 * API: POST /todo/own, PATCH /todo/own/:id (api/ownTodo).
 * Words (en/es): todo.gone, todo.own.notSaved.
 *
 * @module own-todos/useOwnTodos
 */

import { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { addOwnTodo, editOwnTodo, type OwnTodoAnswer, type OwnTodoChange, type OwnTodoNew } from '@/shared/api/ownTodo';
import type { TodoAsk } from '@/shared/api/todo';
import { reportServerFailure, serverFailureText, serverFailureWords } from '@/shared/lib/serverFailure';
import type { TodayBrief, OwnTodoItem } from '@/shared/api/todayBrief';
import { settleAfterNoAnswer, type Written } from './ownTodoLanded';
import { sayTodo, type TodoMarks } from '@/features/today/lib/useTodoMarks';

/** The code the server gives when the to-do is no longer in the garden (routes/todo.js). */
const JOB_NOT_FOUND = 'TODO_JOB_NOT_FOUND';

/**
 * Whether a failure is a 404 that does not carry the server's own "no such to-do" code: a route the server does not
 * have, or any other 404.
 *
 * @param error - Whatever was thrown.
 * @returns True for a 404 without TODO_JOB_NOT_FOUND.
 */
function isUnexpected404(error: unknown): boolean {
  if (!axios.isAxiosError(error) || error.response?.status !== 404) return false;
  return (error.response.data as { error?: string } | undefined)?.error !== JOB_NOT_FOUND;
}

/** What the hook gives the sheet. */
export interface OwnTodos {
  /** Adds a to-do; resolves true once it saved. */
  add: (todo: OwnTodoNew) => Promise<boolean>;
  /** Changes a to-do's title or when; resolves true once it saved. */
  edit: (id: number, change: OwnTodoChange) => Promise<boolean>;
  /** A write is on its way. */
  busy: boolean;
  /** What to say under the button when the last write did not save; null for nothing. */
  failed: string | null;
  /** Forgets the last failure (the sheet opened again). */
  clearFailed: () => void;
  /** The key of the to-do last added or edited, for its group to show and scroll to; null for none. */
  openKey: string | null;
}

/**
 * Adding and editing own to-dos, for a page that shows the list.
 *
 * @param queryKey - The key the brief is kept under (useTodayBrief).
 * @param ask - How the brief was asked for (useTodayBrief).
 * @param marks - The page's useTodoMarks, which the toast's Undo marks through.
 * @returns The writes and their state.
 */
export function useOwnTodos(queryKey: readonly unknown[], ask: TodoAsk, marks: TodoMarks): OwnTodos {
  const { reactTranslate: t } = useTranslation();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const { mark } = marks;
  /** A write is on its way (a ref, so a second tap in the same moment cannot start another). */
  const inFlight = useRef(false);

  /**
   * The own to-dos of the brief held for this page.
   *
   * @returns The brief's todo.own, or [].
   */
  const heldOwn = useCallback((): OwnTodoItem[] => queryClient.getQueryData<TodayBrief>(queryKey)?.todo?.own ?? [], [queryClient, queryKey]);

  /**
   * Makes one write and takes its answer: the toast with its Undo, the key to show, and Today read again in the background.
   *
   * @param write - The request.
   * @param where - For the failure report.
   * @param written - What is written, to look for it in the brief when the write gets no answer.
   * @returns True once it saved.
   */
  const answer = useCallback(async (write: () => Promise<OwnTodoAnswer>, where: string, written: Written): Promise<boolean> => {
    if (inFlight.current) return false;
    inFlight.current = true;
    setBusy(true);
    setFailed(null);
    try {
      const { said } = await write();
      // B73: saved; the sheet closes now and Today is read again behind it (started, not awaited).
      void queryClient.invalidateQueries({ queryKey });
      sayTodo(said, (undo) => { void mark(null, { action: undo.action, key: undo.key }); });
      setOpenKey(said?.openKey ?? null);
      return true;
    } catch (error) {
      const settled = await settleAfterNoAnswer(error, written, async () => {
        await queryClient.refetchQueries({ queryKey }, { throwOnError: true });
        return heldOwn();
      });
      if (settled.result === 'added') {
        setOpenKey(settled.openKey);
        return true;
      }
      const missingRoute = isUnexpected404(error);
      const gone = axios.isAxiosError(error) && error.response?.status === 404 && !missingRoute;
      if (gone) void queryClient.invalidateQueries({ queryKey });
      if (missingRoute) reportServerFailure(error, where);
      setFailed(serverFailureText(error, where) ?? (missingRoute ? serverFailureWords(error) : gone ? t('todo.gone') : t('todo.own.notSaved')));
      return false;
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, [heldOwn, mark, queryClient, queryKey, t]);

  const add = useCallback((todo: OwnTodoNew) => answer(() => addOwnTodo(todo, ask), 'Own to-do add', { kind: 'add', title: todo.title, beforeIds: heldOwn().map((i) => i.id) }), [answer, ask, heldOwn]);
  const edit = useCallback((id: number, change: OwnTodoChange) => answer(() => editOwnTodo(id, change, ask), 'Own to-do edit', { kind: 'edit', id, change }), [answer, ask]);
  const clearFailed = useCallback(() => setFailed(null), []);

  return { add, edit, busy, failed, clearFailed, openKey };
}
